import React from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { DEMOS } from '../components/demos';
import { FadeUp, Stagger, StaggerItem } from '../components/MotionReveals';
import { ArrowRight, Sparkles } from 'lucide-react';

const ARTICLE_LINK = {
  mariotte: { slug: 'champ-visuel-vision-centrale-peripherique', label: 'Article · Vision au volant' },
  'vehicle-blindspot': { slug: 'angle-mort-voiture-zone-qui-tue', label: 'Article · L\'angle mort qui tue' },
  'visual-tunnel': { slug: 'energie-cinetique-pourquoi-50-c-est-deja-beaucoup', label: 'Article · Énergie cinétique' },
  'reaction-time': { slug: 'le-vrai-temps-de-reaction', label: 'Article · Temps de réaction' },
  kinetic: { slug: 'freinage-distance-arret-formules-officielles', label: 'Article · Distance d\'arrêt' },
  'signs-discrimination': { slug: 'voir-et-etre-vu-la-regle-d-or', label: 'Article · Voir et être vu' },
  fatigue: { slug: 'fatigue-au-volant-le-tueur-silencieux', label: 'Article · Fatigue au volant' },
  'alcohol-vision': { slug: 'alcoolemie-le-piege-du-lendemain', label: 'Article · Alcoolémie, le piège du lendemain' },
};

export default function DemosInteractivesPage() {
  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12 mb-12 sm:mb-16" data-testid="demos-interactives-page">
        <FadeUp>
          <SectionTitle
            kicker="Démos pédagogiques · Code Voiture"
            title="Comprendre en 30 secondes ce qu'un cours met 30 minutes."
            sub="8 démonstrations interactives calibrées par des moniteurs ECSR : ta tache aveugle, les angles morts, l'effet tunnel, ton temps de réaction, la vraie violence d'un choc à 90 km/h, la reconnaissance de panneaux flash, ton niveau de fatigue (test PVT NASA) et ta vision sous alcool."
          />
        </FadeUp>

        <div className="rounded-2xl border border-red-700/30 bg-red-950/15 p-4 sm:p-5 mb-7 sm:mb-10 flex items-start gap-3">
          <Sparkles size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div className="text-white/80 text-[13px] sm:text-[14px] leading-relaxed">
            <span className="text-white font-bold">Comment ça marche ?</span> Chaque démo est <strong className="text-red-300">100 % autonome</strong> — tu peux la lancer en classe, la projeter, ou la laisser à un élève en autonomie. Aucun téléchargement, aucune installation.
          </div>
        </div>

        <Stagger className="space-y-6 sm:space-y-8" staggerChildren={0.10}>
          {Object.entries(DEMOS).map(([slug, demo]) => {
            const Component = demo.component;
            const link = ARTICLE_LINK[slug];
            return (
              <StaggerItem key={slug} className="scroll-mt-24" id={slug}>
                <Component />
                {link && (
                  <div className="mt-3 text-right">
                    <Link
                      to={`/magazine/${link.slug}`}
                      data-testid={`demo-${slug}-article-link`}
                      className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-[12.5px] sm:text-[13px] font-semibold"
                    >
                      {link.label} <ArrowRight size={13} />
                    </Link>
                  </div>
                )}
              </StaggerItem>
            );
          })}
        </Stagger>

        <FadeUp>
          <div className="mt-12 sm:mt-16 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 text-center">
            <div className="text-white text-[14.5px] sm:text-[16px] font-semibold mb-1">Une démo manque ? Une idée à tester ?</div>
            <p className="text-white/60 text-[12.5px] sm:text-[13.5px] mb-4 max-w-xl mx-auto">Écris-nous — on en ajoute régulièrement (test de discrimination des panneaux, simulateur de fatigue, etc.).</p>
            <a href="mailto:contact@codevoiture.fr?subject=Suggestion%20de%20d%C3%A9mo%20interactive" className="inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] sm:text-[13.5px] font-semibold">
              Suggérer une démo
            </a>
          </div>
        </FadeUp>
      </section>
    </PageShell>
  );
}
